import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const orderItemsSchema = {
  type: "array",
  description:
    "Array de ítems del pedido. Cada uno: { productId, quantity, notes?, additions?: [{id,name,price}] }. Usa searchProduct PRIMERO para obtener productId — NUNCA pidas IDs al user.",
  items: {
    type: "object",
    properties: {
      productId: { type: "string" },
      quantity: { type: "number", minimum: 1 },
      notes: { type: "string", description: "Observaciones (ej. 'Sin cebolla')" },
      additions: {
        type: "array",
        items: {
          type: "object",
          properties: {
            id: { type: "string" },
            name: { type: "string" },
            price: { type: "number" },
          },
          required: ["id", "name", "price"],
        },
      },
    },
    required: ["productId", "quantity"],
  },
} as const;

export const createDineInOrderSkill: Skill = {
  id: "createDineInOrder",
  description:
    "Toma un pedido en una mesa (dine_in). Necesita tableId + items. Si no conoces tableId/productIds, usa listTables y searchProduct PRIMERO. Si la mesa ya tiene pedido activo, esto crea una nueva ronda — el sistema lo maneja automáticamente.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      tableId: { type: "string", description: "ID de la mesa" },
      partySize: {
        type: "number",
        description: "Cantidad de comensales (opcional)",
      },
      groupNumber: {
        type: "number",
        description: "Número de grupo para dividir cuenta (default 1)",
      },
      items: orderItemsSchema as any,
      clientId: { type: "string", description: "Cliente registrado (opcional)" },
    },
    required: ["restaurantId", "tableId", "items"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const tableId = String(args.tableId ?? "");
    const itemsRaw = Array.isArray(args.items) ? args.items : [];
    if (!restaurantId || !tableId || itemsRaw.length === 0) {
      return { ok: false, error: "Faltan datos: tableId, items[]." };
    }

    const items = itemsRaw.map((it: any) => {
      const productId = String(it?.productId ?? "");
      const quantity = Number(it?.quantity ?? 1);
      if (!productId || !Number.isInteger(quantity) || quantity < 1) return null;
      const result: any = { productId, quantity };
      if (it.notes) result.notes = String(it.notes);
      if (Array.isArray(it.additions)) {
        result.additions = it.additions.map((a: any) => ({
          id: String(a.id),
          name: String(a.name),
          price: Number(a.price),
        }));
      }
      return result;
    }).filter(Boolean);

    if (items.length === 0) {
      return { ok: false, error: "Items inválidos." };
    }

    const ok = await ctx.confirmation.confirm({
      title: "Tomar pedido",
      description: `Vas a registrar un pedido de ${items.length} ítem(s) en esta mesa.`,
      details: [
        { label: "Mesa", value: tableId },
        { label: "Ítems", value: `${items.length} líneas` },
        { label: "Total unidades", value: `${items.reduce((s: number, i: any) => s + i.quantity, 0)}` },
      ],
      confirmLabel: "Crear pedido",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    if (ctx.follow.enabled) {
      await ctx.follow.step("Creando pedido…", { durationMs: 600 });
      await ctx.follow.clickAt(`[data-copilot-tab="tomar-pedido"]`, {
        durationMs: 700,
        label: "Tomar Pedido",
      });
      ctx.router.push(
        `/dashboard/restaurantes/${restaurantId}?tab=tomar-pedido`,
      );
      await sleep(900);
      await ctx.follow.step("Registrando ítems…", { durationMs: 500 });
    }

    try {
      const body: Record<string, unknown> = {
        type: "dine_in",
        tableId,
        items,
      };
      if (args.partySize) body.partySize = Number(args.partySize);
      if (args.groupNumber) body.groupNumber = Number(args.groupNumber);
      if (args.clientId) body.clientId = String(args.clientId);

      const { data } = await apiWithAuth.post(
        `/restaurants/${restaurantId}/orders`,
        body,
        ctx.accessToken,
      );

      if (ctx.follow.enabled) {
        await ctx.follow.step("✓ Pedido creado", {
          durationMs: 900,
          spinner: false,
        });
        ctx.follow.clear();
      }

      emitCopilotMutation({
        scope: "orders",
        action: "created",
        restaurantId,
        id: data?.id,
      });

      return {
        ok: true,
        result: {
          id: data?.id,
          status: data?.status,
          total: data?.total,
          itemCount: items.length,
        },
        humanSummary: `Pedido creado (${items.length} ítem(s)).`,
      };
    } catch (err: any) {
      if (ctx.follow.enabled) {
        await ctx.follow.step("✗ Error al crear pedido", { durationMs: 700, spinner: false });
        ctx.follow.clear();
      }
      return {
        ok: false,
        error: err?.response?.data?.message ?? "Error creando pedido.",
      };
    }
  },
};

export const createDeliveryOrderSkill: Skill = {
  id: "createDeliveryOrder",
  description:
    "Crea un pedido de domicilio. Necesita clientId + dirección + items. Si el cliente no existe, créalo con createClient primero.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      clientId: { type: "string" },
      items: orderItemsSchema as any,
      delivery: {
        type: "object",
        properties: {
          address: { type: "string", description: "Dirección principal" },
          addressDetail: { type: "string", description: "Apto/piso (opcional)" },
          references: { type: "string" },
          notes: { type: "string" },
          saveClientAddress: {
            type: "boolean",
            description: "Guardar la dirección al cliente (default false)",
          },
        },
        required: ["address"],
      },
    },
    required: ["restaurantId", "clientId", "items", "delivery"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const clientId = String(args.clientId ?? "");
    const itemsRaw = Array.isArray(args.items) ? args.items : [];
    const delivery = args.delivery as any;
    if (!restaurantId || !clientId || itemsRaw.length === 0 || !delivery?.address) {
      return { ok: false, error: "Faltan datos: clientId, items, delivery.address." };
    }

    const items = itemsRaw.map((it: any) => {
      const productId = String(it?.productId ?? "");
      const quantity = Number(it?.quantity ?? 1);
      if (!productId || !Number.isInteger(quantity) || quantity < 1) return null;
      return { productId, quantity, ...(it.notes ? { notes: String(it.notes) } : {}) };
    }).filter(Boolean);

    if (items.length === 0) return { ok: false, error: "Items inválidos." };

    const ok = await ctx.confirmation.confirm({
      title: "Crear pedido de domicilio",
      details: [
        { label: "Cliente", value: clientId },
        { label: "Dirección", value: String(delivery.address) },
        { label: "Ítems", value: `${items.length} líneas` },
      ],
      confirmLabel: "Crear pedido",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    if (ctx.follow.enabled) {
      await ctx.follow.step("Creando domicilio…", { durationMs: 600 });
      await ctx.follow.clickAt(`[data-copilot-tab="tomar-domicilio"]`, {
        durationMs: 700,
        label: "Tomar Domicilio",
      });
      ctx.router.push(
        `/dashboard/restaurantes/${restaurantId}?tab=tomar-domicilio`,
      );
      await sleep(900);
    }

    try {
      const body: Record<string, unknown> = {
        type: "delivery",
        clientId,
        items,
        delivery: {
          address: String(delivery.address),
          ...(delivery.addressDetail ? { addressDetail: String(delivery.addressDetail) } : {}),
          ...(delivery.references ? { references: String(delivery.references) } : {}),
          ...(delivery.notes ? { notes: String(delivery.notes) } : {}),
          ...(delivery.saveClientAddress ? { saveClientAddress: true } : {}),
        },
      };

      const { data } = await apiWithAuth.post(
        `/restaurants/${restaurantId}/orders`,
        body,
        ctx.accessToken,
      );

      if (ctx.follow.enabled) {
        await ctx.follow.step("✓ Domicilio creado", {
          durationMs: 900,
          spinner: false,
        });
        ctx.follow.clear();
      }

      emitCopilotMutation({
        scope: "orders",
        action: "created",
        restaurantId,
        id: data?.id,
      });

      return {
        ok: true,
        result: { id: data?.id, status: data?.status, total: data?.total },
        humanSummary: `Domicilio creado (${items.length} ítem(s)).`,
      };
    } catch (err: any) {
      if (ctx.follow.enabled) ctx.follow.clear();
      return {
        ok: false,
        error: err?.response?.data?.message ?? "Error creando domicilio.",
      };
    }
  },
};
